import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type JobProgressionStore } from '@/features/jobs/taxes/progression/JobProgressionStore'
import { type Job } from '@/features/jobs/Job'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { StateEstimatedTaxPaymentEntryViewModel } from '@/features/jobs/details/components/JobProgressionCard/EstimatedIrsPaymentDialog/StateEstimatedTaxPaymentEntryViewModel'

/**
 * Collects the estimated tax payments when accepting an extension.
 */
export class EstimatedIrsPaymentDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The estimated federal payment.
   */
  @observable
  accessor federalAmount = 0

  /**
   * Whether the documents are insufficient to estimate the payments.
   */
  @observable
  accessor insufficientDocuments = false

  /**
   * The estimated payment per state.
   */
  @observable
  accessor stateEntries: StateEstimatedTaxPaymentEntryViewModel[] = []

  constructor(
    private readonly _progressionStore: JobProgressionStore,
    private readonly _job: Job,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Whether a state entry has no state picked.
   */
  @computed
  get hasIncompleteStateEntries() {
    return this.stateEntries.some((e) => !e.isCompleted)
  }

  /**
   * Adds an empty state entry.
   */
  @action.bound
  addStateEntry() {
    this.stateEntries.push(new StateEstimatedTaxPaymentEntryViewModel())
  }

  /**
   * Removes the state entry at the given index.
   */
  @action.bound
  removeStateEntry(index: number) {
    this.stateEntries.splice(index, 1)
  }

  /**
   * Clears the entered payments.
   */
  @action.bound
  reset() {
    this.federalAmount = 0
    this.insufficientDocuments = false
    this.stateEntries = []
  }

  /**
   * Accepts the extension with the entered payments.
   */
  submit = task.resolved(async () => {
    if (!this.insufficientDocuments && this.hasIncompleteStateEntries) {
      return
    }

    const amountByState = this.insufficientDocuments
      ? []
      : this.stateEntries.map((e) => ({
          state: e.state,
          amount: e.amount,
        }))

    await this._progressionStore
      .transitionToStep(this._job.id, {
        step: 'ExtensionAccepted',
        estimatedTaxPayment: {
          federalAmount: this.insufficientDocuments ? 0 : this.federalAmount,
          amountByState,
          isCalculated: !this.insufficientDocuments,
        },
      })
      .catch(this._createAlertErrorHandler('Accepting the extension failed'))
    this.modal.close()
  })
}
